import { useSearchParams } from 'react-router';
import { z } from 'zod';

// react-base/RULES.md § Tables & pagination: page/size always live in URL search params, never
// useState — a server-paginated table must be shareable and reload-safe by URL alone. `prefix`
// namespaces the two params so more than one paginated table can live on the same route (the
// delivery groups / trucks / unassigned-addresses tabs all share one URL).
//
// Default page size 20, options 20 / 50 / 100 (react-base/RULES.md § Tables & pagination); a table
// passes PAGE_SIZE_OPTIONS to components/Basic/Table so its selector offers exactly those.
export const PAGE_SIZE_OPTIONS = [20, 50, 100];

const pageSchema = z.coerce.number().int().min(0).catch(0);
const pageSizeSchema = (defaultPageSize: number) =>
  z.coerce.number().int().min(1).max(100).catch(defaultPageSize);

export const usePageParams = (prefix: string, defaultPageSize = 20) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const pageKey = `${prefix}Page`;
  const pageSizeKey = `${prefix}PageSize`;

  const page = pageSchema.parse(searchParams.get(pageKey) ?? undefined);
  const pageSize = pageSizeSchema(defaultPageSize).parse(
    searchParams.get(pageSizeKey) ?? undefined
  );

  const setPageParams = (nextPage: number, nextPageSize: number) => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.set(pageKey, String(nextPage));
        next.set(pageSizeKey, String(nextPageSize));
        return next;
      },
      { replace: true }
    );
  };

  return { page, pageSize, setPageParams };
};
